import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useModalA11y } from '@/hooks/useModalA11y';
import { t } from '@/locales/strings';
import { safeWrite } from '@/utils/supabaseWrite';
import CTAButton from '@/components/ui/CTAButton';
import LoadingScreen from '@/components/ui/LoadingScreen';
import RingPlayer from '@/components/training/RingPlayer/index';
import RouteHint from '@/components/ui/RouteHint';
import TermPopover from '@/components/ui/TermPopover';
import { applyDifficulty, difficultyLabelKey } from '@/logic/difficulty';
import useMediaSession from '@/hooks/useMediaSession';
import { play as playMusic, stop as stopMusic } from '@/utils/musicCoach';
import styles from './SessionScreen.module.css';

// SessionScreen.module.css owns both the overview wrapper and the
// between-exercise transition. `transitionStyles` is kept as an alias
// on the same module so the transition JSX below reads cleanly.
const transitionStyles = styles;

// Terms most likely to be unfamiliar for each session type. Used
// by the why card on the overview to surface 1–2 contextual
// "?" chips next to the explanation. Keys map to TermPopover
// term keys (see src/locales/strings.js → term_<key>_*).
const RELEVANT_TERMS_BY_TYPE = {
  ENDURANCE:   ['hold', 'layer'],
  POWER:       ['pulse'],
  RELEASE:     ['release', 'push'],
  DIRECTION:   ['layer'],
  INTEGRATION: ['layer', 'breath_line'],
  GENTLE:      ['breath_line'],
};

// ── Design-system icon primitives ──
// Inline SVG, 20x20 canvas, 24x24 viewBox. The SessionScreen is a dark
// surface (`--vr-*` tokens), so bullet cards use translucent white and
// icons use the accent pink rather than the rose used on WeekHome. Two
// colour variants keep the same geometry, swapping only the stroke.
const ICON_BASE = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
// IconAlert keeps its #FF80AB default — used in the dark exit modal
// where the rose --primary would clash with the velvet-rhythm tokens.
// Light-context call sites pass stroke="currentColor" + a parent
// style={{color:'var(--primary)'}} so the rose recolours per theme.
const IconAlert = ({ stroke = '#FF80AB' }) => (
  <svg {...ICON_BASE} stroke={stroke} aria-hidden="true">
    <path d="M12 3L2 20h20L12 3z" />
    <path d="M12 10v4M12 17.5v.5" />
  </svg>
);
const IconShield = ({ stroke = 'currentColor' }) => (
  <svg {...ICON_BASE} stroke={stroke} aria-hidden="true" style={{ color: 'var(--primary)' }}>
    <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const IconWind = ({ stroke = 'currentColor' }) => (
  <svg {...ICON_BASE} stroke={stroke} aria-hidden="true" style={{ color: 'var(--primary)' }}>
    <path d="M3 10h11a3 3 0 1 0-3-3" />
    <path d="M3 14h15a3 3 0 1 1-3 3" />
  </svg>
);
// Dark-theme bullet card for use inside the player overlay (exit modal).
// (DarkBulletCard retired when ExitConfirmOverlay was re-themed to the
// cream / primary-gradient language the rest of the app uses.)

const moodPillStyle = {
  flex: 1,
  textAlign: 'center',
  fontFamily: 'var(--font-medium)',
  fontSize: 12,
  letterSpacing: '0.04em',
  color: 'var(--primary)',
  background: 'var(--primary-pale, #F5D6E0)',
  borderRadius: 999,
  padding: '10px 14px',
  textDecoration: 'none',
  WebkitTapHighlightColor: 'transparent',
};

const xBtnStyle = {
  background: 'none', border: 'none', fontSize: 18,
  color: 'var(--warm-brown)', cursor: 'pointer', padding: '12px',
  minHeight: 44, minWidth: 44,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent',
};

// "Set the mood" card. Sits below the Begin CTA on the session
// overview and gives the user a handle on pairing the practice with
// music. We don't ship licensed audio; instead we open a search in
// the user's preferred service with a curated query that reads as
// editorial rather than algorithmic. The vibe brief lives in
// session_mood_hint so the brand team can tweak the language without
// shipping code.
function SetTheMoodCard({ lang }) {
  const query = encodeURIComponent(t('session_mood_query', lang));
  const spotifyUrl = `https://open.spotify.com/search/${query}`;
  const appleUrl = `https://music.apple.com/search?term=${query}`;
  return (
    <div style={{ padding: '0 20px 16px' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: 'var(--surface, #FFFFFF)',
        border: '1px solid var(--border, #E8DDD4)',
        borderRadius: 14,
        padding: '16px 18px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: 'var(--font-medium)',
          fontSize: 11,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--warm-brown)',
        }}>
          <span aria-hidden="true" style={{
            width: 22, height: 22, borderRadius: 11,
            background: 'var(--primary-pale, #F5D6E0)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--primary)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </span>
          {t('session_mood_label', lang)}
        </div>
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          lineHeight: 1.55,
          color: 'var(--ink, #2d1f26)',
        }}>
          {t('session_mood_hint', lang)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <a
            href={spotifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={moodPillStyle}
          >
            {t('session_mood_open_spotify', lang)}
          </a>
          <a
            href={appleUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={moodPillStyle}
          >
            {t('session_mood_open_apple', lang)}
          </a>
        </div>
      </div>
    </div>
  );
}


// Light-theme bullet card for the session overview (safety note, etc).
function LightBulletCard({ icon, children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      background: 'var(--surface, #FFFFFF)',
      border: '1px solid var(--border, #E8DDD4)',
      borderRadius: 12,
      padding: '14px 16px',
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: 16,
        background: 'var(--primary-pale, #F5D6E0)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </span>
      <span style={{
        flex: 1,
        fontFamily: 'var(--font-body)',
        fontSize: 13,
        color: 'var(--warm-brown)',
        lineHeight: 1.5,
      }}>
        {children}
      </span>
    </div>
  );
}

// DifficultyModal — three-option chooser surfaced from the
// session overview's Adjust pill. Pure presentation; the parent
// owns persistence (patchProgress + safeWrite). Closes on
// backdrop tap, Escape, or selection. The picked value drives
// applyDifficulty on the next session render.
function DifficultyModal({ lang, current, onClose, onPick }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const options = [
    { offset: -1, key: 'difficulty_easier' },
    { offset:  0, key: 'difficulty_standard' },
    { offset:  1, key: 'difficulty_stronger' },
  ];
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(28,14,22,0.5)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: 20,
        animation: 'bb-diff-fade 220ms ease-out',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: '#fff',
        borderRadius: 22,
        padding: 20,
        boxShadow: '0 28px 60px -20px rgba(45,31,38,.45)',
      }}>
        <div style={{
          font: '600 9px/1 var(--font-ui)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
          marginBottom: 8,
        }}>
          {t('session_difficulty_eyebrow', lang)}
        </div>
        <h2 style={{
          margin: '0 0 6px',
          font: '500 22px/1.2 var(--font-title), serif',
          color: 'var(--ink, #2d1f26)',
        }}>
          {t('session_difficulty_title', lang)}
        </h2>
        <p style={{
          margin: '0 0 16px',
          font: '400 14px/1.45 var(--font-body)',
          color: 'var(--ink-dim, #6b5560)',
        }}>
          {t('session_difficulty_subtitle', lang)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {options.map(({ offset, key }) => {
            const isCurrent = offset === current;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onPick(offset)}
                aria-pressed={isCurrent}
                style={{
                  textAlign: 'left',
                  padding: '12px 14px',
                  borderRadius: 14,
                  border: isCurrent
                    ? '1.5px solid var(--primary, #c97a68)'
                    : '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
                  background: isCurrent ? 'var(--primary-pale, #fbeae7)' : '#fff',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <div style={{
                  font: '500 15px/1.2 var(--font-title), serif',
                  marginBottom: 2,
                }}>
                  {t(key + '_title', lang)}
                </div>
                <div style={{
                  font: '400 13px/1.4 var(--font-body)',
                  color: 'var(--ink-dim, #6b5560)',
                }}>
                  {t(key + '_body', lang)}
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            minHeight: 44,
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
            background: 'transparent',
            color: 'var(--ink-dim, #6b5560)',
            font: '600 13px/1 var(--font-ui)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('session_difficulty_close', lang)}
        </button>
      </div>
      <style>{`
        @keyframes bb-diff-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default function SessionScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, refreshProgress, patchProgress } = useApp();
  // Difficulty offset (-1 / 0 / +1) is a user-set preference that
  // scales hold/relax/sec durations on every session. Applied to
  // the session's exercise list before it reaches RingPlayer so
  // the engine itself stays oblivious. Read from location.state's
  // userProgress (which is the live snapshot at session start);
  // see the destructuring below.
  const [showDifficulty, setShowDifficulty] = useState(false);

  const { session, weekData, userProgress, user } = location.state ?? {};
  const isYear1 = (userProgress?.current_year || 1) !== 2;
  const hasSession = !!(session && session.exercises?.length);

  const [phase, setPhase] = useState('overview'); // overview | exercise | transition | saving
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const transitionTimer = useRef(null);

  // OS media session: publishes a "now playing" entry to the lock screen
  // while the user is actively in an exercise. AirPods / car Bluetooth /
  // lock screen play-pause maps to the exit-confirm overlay so a stray
  // tap pauses rather than silently ending the set. Stop does the same
  // thing, just through the explicit OS "stop" button.
  useMediaSession({
    active: phase === 'exercise' || phase === 'transition',
    title: session?.title || session?.exercises?.[exerciseIndex]?.name || 'Be Bold session',
    album: weekData?.theme || session?.meta?.phase || 'Pelvic floor training',
    onPause: () => setShowExitConfirm(true),
    onStop: () => setShowExitConfirm(true),
  });

  useEffect(() => () => clearTimeout(transitionTimer.current), []);

  // Background music. Fades in when the user taps Begin (phase moves
  // off 'overview') and fades out on save / unmount. The musicCoach
  // module silently no-ops if /audio/music/flow.mp3 isn't shipped yet,
  // so this hook is safe regardless of whether tracks exist.
  useEffect(() => {
    if (phase === 'exercise' || phase === 'transition') {
      playMusic('flow');
    } else if (phase === 'saving') {
      stopMusic();
    }
  }, [phase]);
  useEffect(() => () => stopMusic(), []);

  // Transition auto-advance timer. Keep this above the early return so hook
  // order is stable across renders (React rules-of-hooks).
  useEffect(() => {
    if (phase !== 'transition') return;
    transitionTimer.current = setTimeout(goToNextExercise, 2000);
    return () => clearTimeout(transitionTimer.current);
  }, [phase, exerciseIndex]);

  // If /session was opened without navigation state (hard refresh,
  // direct URL), redirect back to /home from an effect — never call
  // navigate() during render, which React Router warns about and which
  // was leaving the route stuck on a blank page.
  useEffect(() => {
    if (!hasSession) {
      console.warn('[SessionScreen] No session in location.state; redirecting to /home', {
        hasLocationState: !!location.state,
        hasSession: !!session,
        exerciseCount: session?.exercises?.length,
      });
      navigate('/home', { replace: true });
    }
  }, [hasSession, navigate, location.state, session]);

  // Apply the user's difficulty offset to the session's exercise
  // list. -1 scales hold/relax/sec to 80%; +1 scales to 120%; 0
  // is a no-op (returns the original array reference, so the
  // common case allocates nothing). Hoisted above the early
  // return so the useMemo call site is unconditional (the lint
  // rule for hook ordering); the empty fallback is fine when we
  // bail out below.
  const difficultyOffset = userProgress?.difficulty_offset ?? 0;
  const exercises = useMemo(
    () => applyDifficulty(session?.exercises, difficultyOffset) || [],
    [session?.exercises, difficultyOffset],
  );

  if (!hasSession) return <LoadingScreen />;

  const currentExercise = exercises[exerciseIndex];

  // ── Handlers ──

  function handleExerciseComplete() {
    if (exerciseIndex < exercises.length - 1) {
      setPhase('transition');
    } else {
      setPhase('saving');
      saveSession();
    }
  }

  function goToNextExercise() {
    clearTimeout(transitionTimer.current);
    setExerciseIndex(i => i + 1);
    setPhase('exercise');
  }

  async function saveSession() {
    // Gentle-return streak policy:
    //   0 days since last  keep streak as is
    //   1 day              +1 (normal increment)
    //   2 or 3 days        preserved at same count (forgiven gap)
    //   4+ days            restart at 1
    //
    // Vacation mode preserves the streak regardless of gap and the
    // returning session is treated as +1 from the frozen baseline.
    function calculateStreak(lastAt, current, vacation) {
      if (vacation) return { streak: (current || 0) + 1, resumed: false };
      if (!lastAt) return { streak: 1, resumed: false };
      const diff = Math.floor((Date.now() - new Date(lastAt).getTime()) / 86400000);
      if (diff <= 0) return { streak: current || 1, resumed: false };
      if (diff === 1) return { streak: (current || 0) + 1, resumed: false };
      if (diff <= 3 && (current || 0) > 0) {
        return { streak: current, resumed: true };
      }
      return { streak: 1, resumed: false };
    }

    const streakResult = calculateStreak(
      userProgress?.last_session_at,
      userProgress?.streak_days,
      !!userProgress?.vacation_mode,
    );
    const newStreak = streakResult.streak;
    let saveError;
    // Personal-best signals are computed inside the try block but
    // need to be visible to navigate() below the catch. Declared
    // here so the eslint no-undef + scoping is correct; defaults
    // mean a thrown try-block produces no false-positive
    // celebrations on the /complete screen.
    let isPersonalBestStreak = false;
    let prevMaxStreak = 0;
    let sessionsMilestone = null;

    try {
      const saved = localStorage.getItem('bb7');
      const data = saved ? JSON.parse(saved) : {};
      data.weekTrainedDays = (data.weekTrainedDays || 0) + 1;
      data.total = (data.total || 0) + 1;
      data.streak = newStreak;
      data.last_session_at = new Date().toISOString();
      // Append today's local-day key to the trainedDays history so
      // Stats / Calendar / heatmap surfaces have a per-day record.
      // The vanilla saveSession() in src/main.js already appends here
      // for legacy users; without this, every React-only user would
      // see Stats render an empty heatmap and "0 longest ever". Dedup
      // by membership check so a same-day re-train does not double up.
      const todayKey = (() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })();
      if (!Array.isArray(data.trainedDays)) data.trainedDays = [];
      if (!data.trainedDays.includes(todayKey)) data.trainedDays.push(todayKey);
      // Training again ends vacation mode. The user explicitly
      // re-entered their practice by tapping Begin, so keeping the
      // flag on would be noise.
      if (data.vacation_mode) data.vacation_mode = false;
      // Mark the gentle-return path so SessionFeeling can show the
      // welcome-back moment and Progress/Calendar can render a soft
      // "resumed" marker without recomputing from dates.
      if (streakResult.resumed) data.streak_resumed_at = data.last_session_at;

      // Personal-best detection — the user's all-time longest
      // streak. Tracked separately from `streak` (which resets on
      // a missed day) so we can quietly celebrate "you broke your
      // record" on /complete without the user having to remember
      // the previous high. `prevMaxStreak` is captured BEFORE the
      // write so SessionComplete can compare against the old value.
      prevMaxStreak = data.max_streak_ever || 0;
      isPersonalBestStreak = newStreak > prevMaxStreak;
      if (isPersonalBestStreak) data.max_streak_ever = newStreak;
      // Round-number total-sessions milestones (10, 25, 50, 100,
      // 250, 500, 1000) — the data is already there in `data.total`.
      const totalAfter = data.total;
      const ROUND = [10, 25, 50, 100, 250, 500, 1000];
      sessionsMilestone = ROUND.includes(totalAfter) ? totalAfter : null;

      // Year 1: advance the 1..365 day pointer so WeekHome loads the next
      // day's session on return. Year 2 still drives advancement via
      // weekTrainedDays + current_week (untouched).
      if (isYear1) {
        data.current_day = Math.min((data.current_day || 1) + 1, 365);
      }

      const result = await safeWrite(user?.id, data);
      saveError = !result;

      // Without this, React's userProgress stays stale and WeekHome's
      // useEffect never re-fires — the user would see yesterday's session
      // on return to /home.
      if (result) {
        try { await refreshProgress(); } catch {}
      }
    } catch {
      saveError = true;
    }

    // Recap stats — honest, observable counts only. Anything
    // that would require breath / release telemetry would be
    // fabrication; what we ship is "you did N things over Y
    // minutes," which is true and concrete.
    const exerciseCount = exercises?.length || 0;
    const sessionMinutes = isYear1
      ? 5
      : (session?.type === 'INTEGRATION' ? 9 : 5);

    navigate('/session-feeling', {
      state: {
        session, weekData, newStreak, saveError,
        resumed: streakResult.resumed,
        // Personal-best signals. Captured pre-write so the
        // /complete screen can compare against the old high.
        // prevMaxStreak === 0 means "no record yet"; we only
        // celebrate when the user has at least 3 days behind
        // them so day-1 doesn't trip the message.
        isPersonalBestStreak: isPersonalBestStreak && newStreak >= 3,
        prevMaxStreak,
        sessionsMilestone,
        // Recap stats for the "what just happened" bullets on
        // /complete. Two truthful counts; no derived claims.
        exerciseCount,
        sessionMinutes,
      },
      replace: true,
    });
  }

  // ── OVERVIEW ──

  if (phase === 'overview') {

    console.log('[SessionScreen] rendering overview', {
      isYear1,
      sessionDay: session?.day,
      sessionTitle: session?.title,
      exerciseCount: session?.exercises?.length,
      sessionType: session?.type,
    });
    const movementNames = {
      SQUEEZE:   { en: 'Squeeze', pt: 'Apertar' },
      RELAX:     { en: 'Relax', pt: 'Soltar' },
      PUSH:      { en: 'Push', pt: 'Empurrar' },
      DIRECTION: { en: 'Direction', pt: 'Direcao' },
    };

    // Path B early-phase gating: Year 2 uses first 4 weeks; Year 1 uses month 1.
    // Path lives on userProgress (AppContext maps vanilla `data.path`), not
    // on the Supabase auth user object.
    const isPathBEarly = userProgress?.path === 'B' && (
      isYear1 ? (session.meta?.month === 1) : ((userProgress?.current_week || 1) <= 4)
    );

    // Year 1 has no session.type, so the legacy header label would crash on
    // undefined.toLowerCase(). Year 1 shows the day title instead.
    const headerLabel = isYear1
      ? (lang === 'pt' ? session.title_pt : session.title)
      : t('session_type_' + session.type.toLowerCase(), lang);
    const headerDuration = isYear1 ? '5 min' : (session.type === 'INTEGRATION' ? '9 min' : '5 min');

    return (
      <div className={styles.overviewScreen}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
          <button type="button" onClick={() => navigate('/home', { replace: true })} style={xBtnStyle} aria-label={t('session_close_aria', lang)}>{'\u2715'}</button>
          <span style={{ fontFamily: 'var(--font-medium)', fontSize: 13, color: 'var(--dark-brown)', textTransform: 'uppercase' }}>
            {headerLabel}
          </span>
          <span style={{ fontFamily: 'var(--font-light)', fontSize: 13, color: 'var(--warm-brown)' }}>
            {headerDuration}
          </span>
        </div>

        {/* First-visit player orientation. Sits between the header
            and the session title so it is the first thing a new
            user reads after pressing Start. Fires exactly once. */}
        <RouteHint
          hintKey="session"
          titleKey="route_hint_session_title"
          bodyKey="route_hint_session_body"
          lang={lang}
        />

        {/* Session title — editorial moment before the exercise list.
            Eyebrow is the phase / week label, the Fraunces italic gives
            the session a name, and the underline rule anchors the block
            as a chapter heading. */}
        <div className="motion-rise" style={{ padding: '4px 20px 20px', textAlign: 'center', '--stagger-i': 0 }}>
          <div style={{
            fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'var(--warm-brown)', marginBottom: 10,
          }}>
            {isYear1 ? (
              <>
                {t('day_of_year_label', lang).replace('{n}', session.day).replace('{total}', 365)}
                {' \u00B7 '}
                {lang === 'pt' ? session.meta?.phase_pt : session.meta?.phase}
              </>
            ) : weekData ? (
              <>
                {t('week_label', lang).replace('{n}', userProgress?.current_week || 1)}
                {' \u00B7 '}
                {lang === 'pt' ? weekData.phase_pt : weekData.phase}
              </>
            ) : null}
          </div>
          <h1 style={{
            fontFamily: 'var(--font-title)', fontStyle: 'italic', fontSize: 30,
            fontWeight: 500, color: 'var(--dark-brown)',
            margin: 0, lineHeight: 1.1, letterSpacing: '-0.02em',
            fontVariationSettings: '"opsz" 96',
          }}>
            {isYear1
              ? (lang === 'pt' ? session.title_pt : session.title)
              : (weekData ? (lang === 'pt' ? weekData.theme_pt : weekData.theme) : '')}
          </h1>
        </div>

        {/* "Why" card — one-sentence plain-language anchor for
            the session, sourced from session_why_<type>. Renders
            between the title and the exercise list so the user
            reads it before scanning the list of exercises. Skipped
            when the session has no recognisable type (Year 1 day
            sessions get their own copy via session.title). */}
        {!isYear1 && session.type && (
          <div className="motion-rise stagger-item" style={{
            margin: '0 20px 20px',
            padding: '14px 16px',
            borderRadius: 14,
            background: 'var(--cream, #fbeae7)',
            borderLeft: '3px solid var(--primary, #c97a68)',
            '--stagger-i': 1,
          }}>
            <div style={{
              fontFamily: 'var(--font-ui)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.32em', textTransform: 'uppercase',
              color: 'var(--primary-dark, #a85c4b)', marginBottom: 4,
            }}>
              {t('session_why_eyebrow', lang)}
            </div>
            <p style={{
              margin: 0,
              fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.5,
              color: 'var(--ink, #2d1f26)',
              textWrap: 'balance',
            }}>
              {t('session_why_' + session.type.toLowerCase(), lang)}
            </p>
            {/* Contextual term chips — 1-2 small "?" buttons that
                open a TermPopover for the most relevant clinical
                words for this session type. The user gets the
                plain explanation in one tap; the why card stays
                short. */}
            {(RELEVANT_TERMS_BY_TYPE[session.type] || []).length > 0 && (
              <div style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 10,
              }}>
                {RELEVANT_TERMS_BY_TYPE[session.type].map((termKey) => (
                  <span key={termKey} style={{
                    fontSize: 12,
                    fontFamily: 'var(--font-body)',
                    color: 'var(--primary-dark, #a85c4b)',
                  }}>
                    <TermPopover termKey={termKey} lang={lang}>
                      {t(`term_${termKey}_title`, lang)}
                    </TermPopover>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Exercise cards */}
        <div style={{ padding: '0 20px', marginBottom: 24 }}>
          {exercises.map((ex, i) => {
            const name = movementNames[ex.movement] || { en: ex.movement, pt: ex.movement };
            const isSqueeze = ex.movement === 'SQUEEZE';
            const dimmed = isPathBEarly && isSqueeze;

            let detail;
            if (ex.movement === 'RELAX' && ex.reps) {
              detail = `${ex.reps} ${t('session_breath_cycles', lang)}`;
            } else if (ex.type === 'pulse') {
              detail = `${ex.sets}x${ex.reps}`;
            } else if (isYear1) {
              // Year 1 keeps the line as bare as the RingPlayer chip:
              // verb + duration. Sets / reps / rest are handled inside
              // the player; here all the user needs to see is which of
              // SQUEEZE / RELAX / PUSH is up next and how long it
              // holds for.
              const verbKey =
                ex.movement === 'PUSH' ? 'rp_state_push'
                  : ex.movement === 'RELAX' ? 'rp_state_release'
                  : 'rp_state_squeeze';
              const verb = t(verbKey, lang);
              detail = ex.hold ? `${verb} \u00B7 ${ex.hold}s` : verb;
            } else {
              detail = `${ex.sets} ${t('session_sets', lang)} \u00B7 ${ex.hold}s`;
            }

            const moveColor = isSqueeze ? '#8B4A5A' : ex.movement === 'RELAX' ? '#7A5A6A' : '#4a6a80';

            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 0', borderBottom: i < exercises.length - 1 ? '1px solid var(--beige)' : 'none',
                opacity: dimmed ? 0.4 : 1,
              }}>
                <div style={{ width: 4, height: 40, borderRadius: 2, background: moveColor, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text)' }}>
                    {lang === 'pt' ? name.pt : name.en}
                  </div>
                  <div style={{ fontFamily: 'var(--font-light)', fontSize: 12, color: 'var(--warm-brown)', marginTop: 2 }}>
                    {detail}
                  </div>
                  {dimmed && (
                    <div style={{ fontFamily: 'var(--font-light)', fontSize: 11, color: 'var(--relax)', marginTop: 4 }}>
                      {t('session_path_b_banner', lang)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Difficulty pill — quiet outline button that opens a
            three-option modal (easier / standard / stronger).
            Choice persists to user_progress.difficulty_offset
            and applies to every future session via
            applyDifficulty above. Sits above the Begin CTA so a
            user who feels yesterday was too hard / too easy can
            adjust before pressing go. */}
        <div style={{ padding: '0 20px 12px', display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => setShowDifficulty(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 14%, transparent)',
              background: 'transparent',
              color: 'var(--ink-dim, #6b5560)',
              font: '500 12px/1 var(--font-ui)',
              letterSpacing: '0.04em',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 6h12M4 12h8M4 18h16" />
              <circle cx="18" cy="6" r="2" fill="currentColor" />
              <circle cx="14" cy="12" r="2" fill="currentColor" />
              <circle cx="20" cy="18" r="2" fill="currentColor" />
            </svg>
            {t('session_difficulty_label', lang)}
            {' · '}
            {t(difficultyLabelKey(difficultyOffset), lang)}
          </button>
        </div>

        {/* CTA */}
        <div style={{ padding: '0 20px 16px' }}>
          <CTAButton
            label={t('session_begin_cta', lang)}
            onClick={() => { setPhase('exercise'); setExerciseIndex(0); }}
          />
        </div>

        {/* Set the mood — pair the practice with music. Opens a
            search query in the user's preferred service so she
            picks her own playlist; we only set the vibe. */}
        <SetTheMoodCard lang={lang} />

        {/* Safety note — bullet cards per the design system. Each
            cautionary statement gets its own rose-disc icon so the row
            scans as a checklist rather than a wall of fine print. */}
        <div style={{ padding: '0 20px 32px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LightBulletCard icon={<span style={{ color: 'var(--primary)' }}><IconAlert stroke="currentColor" /></span>}>
            {t('session_safety_bullet_1', lang)}
          </LightBulletCard>
          <LightBulletCard icon={<IconWind />}>
            {t('session_safety_bullet_2', lang)}
          </LightBulletCard>
          <LightBulletCard icon={<IconShield />}>
            {t('session_safety_bullet_3', lang)}
          </LightBulletCard>
        </div>

        {/* Difficulty modal — three-option chooser the user opens
            from the pill above the Begin CTA. Saves on tap and
            closes; in-flight session uses whatever was set
            BEFORE the user pressed Begin (a change made during
            an active session would not retroactively rescale).
            Fire-and-forget save: patchProgress flips the
            in-memory value the next exercises useMemo reads. */}
        {showDifficulty && (
          <DifficultyModal
            lang={lang}
            current={difficultyOffset}
            onClose={() => setShowDifficulty(false)}
            onPick={(offset) => {
              try {
                const saved = localStorage.getItem('bb7');
                const data = saved ? JSON.parse(saved) : {};
                data.difficulty_offset = offset;
                safeWrite(user?.id, data).catch(() => {});
                patchProgress({ difficulty_offset: offset });
              } catch {}
              setShowDifficulty(false);
            }}
          />
        )}
      </div>
    );
  }

  // ── EXERCISE ──

  if (phase === 'exercise') {
    return (
      <div>
        {/* Progress dots — only on Year 2 (dark player). Year 1's cream
            RingPlayer has its own top bar and a dark progress bar would
            clash above it. */}
        {!isYear1 && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 210,
            display: 'flex', justifyContent: 'center', gap: 6,
            padding: '12px 0', paddingTop: 'max(12px, env(safe-area-inset-top))',
            background: 'var(--vr-bg, #1a0a12)',
          }}>
            {exercises.map((_, i) => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: 4,
                background: i < exerciseIndex ? 'var(--vr-accent, #FF80AB)'
                  : i === exerciseIndex ? 'var(--vr-text, #fff0f5)' : 'rgba(255,255,255,0.15)',
              }} />
            ))}
          </div>
        )}

        {/* Both Year 1 and Year 2 now route through RingPlayer —
            the cream canal / breath / flower language applies
            across the whole journey. Y1 keeps the basic
            SQUEEZE / PUSH / RELAX vocabulary; Y2 unlocks the
            eight named skills (LOCK / GRIP / PULL / TILT / TWIST
            / DRAW / LIFT / PULSE) via the skillVocab prop. */}
        <RingPlayer
          exercise={currentExercise}
          lang={lang}
          onComplete={handleExerciseComplete}
          onExit={() => setShowExitConfirm(true)}
          onSkip={handleExerciseComplete}
          exerciseIndex={exerciseIndex}
          totalExercises={exercises.length}
          skillVocab={(userProgress?.current_year || 1) >= 2 ? 'advanced' : 'basic'}
        />

        {/* Exit confirmation — dark-theme bullet card per the design
            system. Single sentence gets one card with an alert icon so
            the warning lands visually without a wall of plain text. */}
        <ExitConfirmOverlay
          open={showExitConfirm}
          onLeave={() => navigate('/home', { replace: true })}
          onKeep={() => setShowExitConfirm(false)}
          lang={lang}
        />
      </div>
    );
  }

  // ── TRANSITION ──

  if (phase === 'transition') {
    const doneName = currentExercise?.movement || '';
    const nextEx = exercises[exerciseIndex + 1];
    const nextName = nextEx?.movement || '';
    const movementNames = { SQUEEZE: { en: 'Squeeze', pt: 'Apertar' }, RELAX: { en: 'Relax', pt: 'Soltar' }, PUSH: { en: 'Push', pt: 'Empurrar' }, DIRECTION: { en: 'Direction', pt: 'Direcao' } };
    const doneLabel = lang === 'pt' ? (movementNames[doneName]?.pt || doneName) : (movementNames[doneName]?.en || doneName);
    const nextLabel = lang === 'pt' ? (movementNames[nextName]?.pt || nextName) : (movementNames[nextName]?.en || nextName);

    return (
      <div className={transitionStyles.screen}>
        <div className={transitionStyles.bloom} aria-hidden="true">
          <span className={transitionStyles.bloomRing} />
          <span className={`${transitionStyles.bloomRing} ${transitionStyles['bloomRing--mid']}`} />
          <span className={`${transitionStyles.bloomRing} ${transitionStyles['bloomRing--inner']}`} />
          <span className={transitionStyles.bloomCheck}>
            <svg viewBox="0 0 44 44" fill="none">
              <path
                d="M11 22 L19 30 L33 14"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        <div className={transitionStyles.doneName}>{doneLabel}</div>

        {nextEx && (
          <>
            <div className={transitionStyles.upNextLabel}>{t('session_up_next', lang)}</div>
            <div className={transitionStyles.upNextName}>{nextLabel}</div>
          </>
        )}

        <div className={transitionStyles.cta}>
          <CTAButton label={t('session_continue', lang)} onClick={goToNextExercise} variant="secondary" />
        </div>
      </div>
    );
  }

  // ── SAVING ──

  return <LoadingScreen />;
}

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 250,
  background: 'rgba(28, 14, 22, 0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  paddingTop: 'max(20px, env(safe-area-inset-top))',
  paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
  paddingLeft: 'max(20px, env(safe-area-inset-left))',
  paddingRight: 'max(20px, env(safe-area-inset-right))',
};

// ExitConfirmOverlay — extracted so useModalA11y can hook at the
// component root (hooks can't be called conditionally from the
// parent JSX). Escape key closes via onKeep; focus is trapped so a
// mid-session Tab never escapes back to the player beneath.
function ExitConfirmOverlay({ open, onLeave, onKeep, lang }) {
  const dialogRef = useModalA11y({ open, onClose: onKeep });
  if (!open) return null;
  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-exit-confirm-title"
      style={overlayStyle}
    >
      <h2
        id="session-exit-confirm-title"
        style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {t('session_exit_confirm_bullet_1', lang)}
      </h2>
      <div style={{
        width: 'min(340px, calc(100vw - 40px))',
        padding: '22px 22px 18px',
        background: 'var(--surface, #fff)',
        borderRadius: 22,
        boxShadow: 'var(--shadow-overlay)',
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{
          font: '500 18px/1.25 var(--font-title, Fraunces), serif',
          color: 'var(--ink, #2d1f26)',
          textWrap: 'balance',
        }}>
          {t('session_exit_confirm_bullet_1', lang)}
        </div>
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button
            type="button" onClick={onLeave}
            style={{
              flex: 1,
              minHeight: 46,
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 14%, transparent)',
              background: 'transparent',
              color: 'var(--ink-dim, #6b5560)',
              font: '600 13px/1 var(--font-ui, Manrope)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('session_exit_leave', lang)}
          </button>
          <button
            type="button" onClick={onKeep}
            style={{
              flex: 1.4,
              minHeight: 46,
              borderRadius: 999,
              border: 'none',
              background: 'linear-gradient(180deg, var(--primary-light) 0%, var(--primary) 100%)',
              color: '#fff',
              font: '600 14px/1 var(--font-ui, Manrope)',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: '0 10px 20px -10px color-mix(in srgb, var(--primary) 55%, transparent)',
            }}
          >
            {t('session_exit_keep', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
